// This controls the Accept/Decline and other buttons in the button stack for Events Pages
import $ from 'jquery'
import SyncView from 'view/sync_view'
import io from 'service/io'
import dispatcher from 'service/event_dispatcher'
import dataProvider from 'service/data_provider'
import stickyFlashMessage from 'shared/view/sticky_flash_message'
import locationService from 'service/location'
import ModalView from 'component/modal/modal'
import PaywallView from 'component/paywall/paywall'
import FeedbackView from 'bundle/event_feedback/view/feedback'
import JoinWaitingListView from 'bundle/event_guestlist/view/join_waiting_list_modal'
import PrintGuestlistView from 'bundle/event_guestlist/view/print_guestlist_modal'

const ATTENDANCE_ACCEPT_SUCCESS_EVENT = 'Attendance:accept:success'
const ATTENDANCE_DECLINE_SUCCESS_EVENT = 'Attendance:decline:success'
const EVENT_SUCCESSFULLY_UNBOOKMARKED = 'Event:unbookmarked:success'
const EVENT_SUCCESSFULLY_BOOKMARKED = 'Event:bookmarked:success'

const EventDetailsButtonStackView = SyncView.extend({
    inProgress: false,
    afterInitialize: $.noop,

    options: {
        defaultErrorMessage: 'Sorry, an error occurred. Please reload the page and try again.',
        buttonLoadingText: 'please wait...',
        bookmarkedSuccessfully: 'Saved successfully!',
        unbookmarkedSuccessfully: 'Unsaved successfully!',
    },

    events: {
        'click .js-attendance-toggle': '_handleAttendanceEvents',
        'click .js-attendance-decline': '_handleAttendanceEvents',
        'click .js-leave-waiting-list': '_handleAttendanceEvents',
        'click .js-event-bring-friends': '_handleBringFriendsButtonClick',
        'click .js-event-attend-confirmation': '_handleAttendEventConfirmationClick',
        'click .js-calendar-bring-friends-accept': '_handleBringFriendsAcceptClick',
        'click .js-rate-button': '_handleBringFriendsButtonClick',
        'click .js-waiting-list-join': '_handleWaitingListJoinClick',
        'click .js-print-guestlist-trigger': '_onPrintGuestlistTriggerClick',
        'click .js-bookmark': '_handleBookmarkingToggle',
        'click .js-unbookmark': '_handleBookmarkingToggle',
    },

    bubbleEvents: {
        'FeedbackModal:submit': '_handleFeedbackSubmission',
        'JoinWaitingListModal:submit': 'handleJoinWaitingListSubmit',
    },

    initialize() {
        SyncView.prototype.initialize.call(this)
        this.model.on('sync', this.handleSync)
        this.afterInitialize()
        this._initializePaywall()
        this._initializeFeedback()
    },

    _initializePaywall() {
        this.initSubview(PaywallView, {
            el: this.$('.js-paywall-attendance'),
            upgradeHandler: 'event',
            upgradeHandlerParameters: { eventId: dataProvider.get('eventPromotion').eventId },
        })
    },

    _initializeFeedback() {
        this.initSubview(FeedbackView, {
            el: this.$('.js-event-feedback-rate'),
            entityId: this.options.entityId,
            entityType: this.options.entityType,
        })
    },

    _handleWaitingListJoinClick() {
        this.initSubview(JoinWaitingListView, {
            el: this.$('.js-waiting-list-join'),
        })
    },

    _onPrintGuestlistTriggerClick(event) {
        this.initSubview(PrintGuestlistView, {
            el: this.$('.js-print-guestlist-trigger'),
            url: $(event.target).data('href'),
        })
    },

    _handleFeedbackSubmission() {
        this.update()
    },

    _handleAttendanceEvents(e) {
        const $target = $(e.target)
        const accepted = $target.data('method') === 'accept'
        const method = accepted ? 'PUT' : 'DELETE'

        e.preventDefault()
        this.handleAttendanceUpdate($target, accepted, method)
    },

    _handleBookmarkingToggle(e) {
        let $target = $(e.target)
        if ($target.hasClass('icon')) {
            $target = $target.parent()
        }
        e.preventDefault()
        const isBookmarking = $target.data('method') === 'bookmark'
        const action = $target.data('href')
        this.showBookmarkSpinner($target)
        let calendarEntryId, calendarEntryType
        if ($target.hasClass('js-activity')) {
            if (dataProvider.get('activity')) {
                calendarEntryId = dataProvider.get('activity').activityId
            } else {
                const calendarEntryIdRegex = /calendarEntryId=([\d]+)|activity\/([\d]+)/gi
                const result = calendarEntryIdRegex.exec(action)
                calendarEntryId = result[1]
            }
            calendarEntryType = 'activity'
        } else {
            calendarEntryId = dataProvider.get('eventPromotion').eventId
            calendarEntryType = 'event'
        }
        if (isBookmarking) {
            this.doBookmarkRequest(action, $target, calendarEntryId, calendarEntryType)
        } else {
            this.doUnbookmarkRequest(action, $target, calendarEntryId, calendarEntryType)
        }
    },

    handleJoinWaitingListSubmit($target, accepted, method) {
        if ($target.hasClass('js-paywall-waiting-list')) {
            this.initSubview(PaywallView, {
                el: $target,
                upgradeHandler: 'event',
                upgradeHandlerParameters: { eventId: dataProvider.get('eventPromotion').eventId },
            }).open()

            return
        }

        this.handleAttendanceUpdate($target, accepted, method)
    },

    /**
     * Fires an ajax request to update the attendance status of the current user.
     * If successful, the header will be completely re-rendered (we do this so we
     * can re-use the logic from our backend template for the showing/hiding of
     * the header buttons)
     * @return {jqXHR} a jquery promise for the ajax request fired
     */
    handleAttendanceUpdate($target, accepted, method) {
        const that = this

        const action = $target.data('href')

        if (!action || this.inProgress) {
            return
        }

        this.inProgress = true

        // Disables the button and shows a "loading" message
        this.disableButton($target)

        return io
            .post(action, { _method: method, _token: dataProvider.get('csrfToken') }, null, 'json')
            .done(() => {
                that.inProgress = false

                if (accepted) {
                    // A special paywall that appears AFTER the action, must check for it explicitly here.
                    // The paywall is shown for basic members who sign up for a paid event.
                    if ($target.hasClass('js-paywall-eventPricing')) {
                        const paywall = that.initSubview(PaywallView, {
                            el: $target,
                            modalOptions: ModalView.getSubData($target),
                            paywallType: 'event',
                            upgradeHandler: 'event',
                            upgradeHandlerParameters: { eventId: dataProvider.get('eventPromotion').eventId },
                            referrer: locationService.getCurrentRelativeUrl(),
                        })
                        paywall.open()
                    }

                    dispatcher.dispatch(ATTENDANCE_ACCEPT_SUCCESS_EVENT)
                } else {
                    dispatcher.dispatch(ATTENDANCE_DECLINE_SUCCESS_EVENT)
                }

                that.trigger('attendance:update', { accepted })
                that.update()
            })
            .fail(this.onServerError)
    },

    /**
     * Handle server errors
     */
    onServerError(response) {
        if (io.requestIsAbortedOrClosed(response)) {
            // Aborted, no need to render errors
            return
        }
        stickyFlashMessage.show(this.options.defaultErrorMessage, { type: 'error' })
    },

    disableButton($button) {
        const loadingText = this.options.buttonLoadingText

        $button.data('original-button-text', $button.text())
        $button.text(loadingText)
        $button.addClass('is-disabled')
    },

    handleSync() {
        dispatcher.dispatch('redraw', this.$el)
    },

    showBookmarkSpinner($button) {
        $button.children('.icon').addClass('is-hidden')
        $button.children('.spinner').removeClass('is-hidden')
    },

    hideBookmarkSpinner($button) {
        $button.children('.icon').removeClass('is-hidden')
        $button.children('.spinner').addClass('is-hidden')
    },

    doBookmarkRequest(action, $target, calendarEntryId, calendarEntryType) {
        const data = {
            calendarEntryId,
            calendarEntryType,
            $target_method: 'POST',
            _token: dataProvider.get('csrfToken'),
        }
        return io
            .post(action, data, null, 'json')
            .done(() => {
                dispatcher.dispatch(EVENT_SUCCESSFULLY_BOOKMARKED)
                this.postBookmarkOrUnbookmarkRequest($target, true)
            })
            .fail(() => {
                this.onServerError()
                this.hideBookmarkSpinner($target)
            })
    },

    doUnbookmarkRequest(action, $target, calendarEntryId, calendarEntryType) {
        const settings = {
            calendarEntryId,
            calendarEntryType,
            method: 'DELETE',
            _token: dataProvider.get('csrfToken'),
            dataType: 'json',
        }
        return io.ajax(action, settings).then(
            () => {
                dispatcher.dispatch(EVENT_SUCCESSFULLY_UNBOOKMARKED)
                this.postBookmarkOrUnbookmarkRequest($target, false)
            },
            () => {
                this.onServerError()
                this.hideBookmarkSpinner($target)
            }
        )
    },

    postBookmarkOrUnbookmarkRequest($button, isBookmarking) {
        if (isBookmarking) {
            $('.js-bookmark-container').addClass('is-hidden').addClass('is-hidden')
            $('.js-unbookmark-container').removeClass('is-hidden')
            stickyFlashMessage.show(this.options.bookmarkedSuccessfully, { type: 'success' })
        } else {
            $('.js-bookmark-container').removeClass('is-hidden')
            $('.js-unbookmark-container').addClass('is-hidden')
            stickyFlashMessage.show(this.options.unbookmarkedSuccessfully, { type: 'success' })
        }
        this.hideBookmarkSpinner($button)
    },
})

export default EventDetailsButtonStackView
